import { useState } from 'react';
import type { CreativeResult, CreativeIdea, SourceCitation } from '../../shared/types';
import { CREATIVE_CHANNEL_OPTIONS } from '../../shared/types';

interface Props {
  creativeResult: CreativeResult;
  sources: SourceCitation[];
  onNewAnalysis: () => void;
  onDeepAnalyze: (idea: CreativeIdea) => void;
}

function RiskBadge({ score, label }: { score: number; label: 'Vert' | 'Jaune' }) {
  const isGreen = label === 'Vert';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
        isGreen
          ? 'bg-emerald-200 text-ink'
          : 'bg-amber-200 text-ink'
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${isGreen ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      {score}/100
    </span>
  );
}

function IdeaCard({ idea, index, onDeepAnalyze }: { idea: CreativeIdea; index: number; onDeepAnalyze: (idea: CreativeIdea) => void }) {
  const [isExpanded, setIsExpanded] = useState(index === 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-cream-card overflow-hidden transition-all">
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-violet-100 flex items-center justify-center text-sm font-bold text-violet-600">
            {index + 1}
          </span>
          <div className="min-w-0">
            <h3 className="font-display text-slate-900 truncate">
              {idea.territory}
            </h3>
            <p className="text-sm text-slate-500 truncate">
              {idea.concept.slice(0, 80)}{idea.concept.length > 80 ? '...' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
          <RiskBadge score={idea.riskScore} label={idea.riskLabel} />
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
          {/* Concept */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
              Concept
            </h4>
            <p className="text-slate-700">
              {idea.concept}
            </p>
          </div>

          {/* Slogans */}
          {idea.slogans.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                Slogans
              </h4>
              <div className="space-y-2">
                {idea.slogans.map((slogan, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100"
                  >
                    <span className="text-violet-500 font-bold text-sm">{i + 1}.</span>
                    <span className="text-slate-800 font-medium italic">
                      &laquo;&nbsp;{slogan}&nbsp;&raquo;
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Visual direction */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
              Direction artistique
            </h4>
            <p className="text-slate-600 text-sm">
              {idea.visualDirection}
            </p>
          </div>

          {/* Legal basis */}
          {idea.legalBasis.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">
                Base l&eacute;gale (ce qui autorise)
              </h4>
              <ul className="space-y-1">
                {idea.legalBasis.map((ref, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                    {ref}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Execution notes */}
          {idea.executionNotes && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                Notes d&apos;ex&eacute;cution
              </h4>
              <p className="text-slate-600 text-sm whitespace-pre-line">
                {idea.executionNotes}
              </p>
            </div>
          )}

          {/* Deep analysis CTA */}
          <div className="pt-2">
            <button
              onClick={(e) => { e.stopPropagation(); onDeepAnalyze(idea); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border-2 border-dashed border-violet-300 text-violet-600 hover:bg-violet-50 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
                <path d="M11 8v6" />
                <path d="M8 11h6" />
              </svg>
              Analyser cette id&eacute;e avec le pipeline 3 juges
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CreativeResults({ creativeResult, sources, onNewAnalysis, onDeepAnalyze }: Props) {
  const { ideas, legalFramework, briefEcho } = creativeResult;
  const channelLabel = CREATIVE_CHANNEL_OPTIONS.find(c => c.value === briefEcho.channel)?.label ?? briefEcho.channel;

  return (
    <div className="animate-fade-in max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-200 text-ink text-sm font-medium mb-4">
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M15.98 1.804a1 1 0 00-1.96 0l-.24 1.192a1 1 0 01-.784.785l-1.192.238a1 1 0 000 1.962l1.192.238a1 1 0 01.785.785l.238 1.192a1 1 0 001.962 0l.238-1.192a1 1 0 01.785-.785l1.192-.238a1 1 0 000-1.962l-1.192-.238a1 1 0 01-.785-.785l-.238-1.192zM6.949 5.684a1 1 0 00-1.898 0l-.683 2.051a1 1 0 01-.633.633l-2.051.683a1 1 0 000 1.898l2.051.684a1 1 0 01.633.632l.683 2.051a1 1 0 001.898 0l.683-2.051a1 1 0 01.633-.633l2.051-.683a1 1 0 000-1.898l-2.051-.683a1 1 0 01-.633-.633L6.95 5.684z" />
          </svg>
          {ideas.length} id&eacute;es cr&eacute;atives g&eacute;n&eacute;r&eacute;es
        </div>
        <h2 className="font-display text-2xl text-slate-900 mb-2">
          Visaly Cr&eacute;a &mdash; {briefEcho.brand}
        </h2>
        <p className="text-slate-500">
          {briefEcho.product} &middot; {channelLabel} &middot; Cible : {briefEcho.target}
        </p>
      </div>

      {/* Legal framework summary */}
      {legalFramework && (
        <div className="mb-6 p-4 rounded-2xl bg-slate-50 border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-1">
            Cadre l&eacute;gal applicable
          </h3>
          <p className="text-sm text-slate-600">
            {legalFramework}
          </p>
        </div>
      )}

      {/* Ideas */}
      <div className="space-y-3 mb-8">
        {ideas.map((idea, idx) => (
          <IdeaCard key={idx} idea={idea} index={idx} onDeepAnalyze={onDeepAnalyze} />
        ))}
      </div>

      {/* Sources */}
      {sources.length > 0 && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Sources juridiques
          </h3>
          <div className="space-y-2">
            {sources.map((src, i) => (
              <div
                key={i}
                className="text-sm text-slate-600 flex items-start gap-2"
              >
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-slate-200 text-slate-500 flex-shrink-0">
                  {src.type}
                </span>
                <span>{src.title} — {src.reference}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="flex justify-center gap-4">
        <button
          onClick={onNewAnalysis}
          className="btn-ink px-6 py-3 text-sm"
        >
          Nouveau brief
        </button>
      </div>
    </div>
  );
}
